/* Desktop viewport bleed for the theme's product/collection carousels.
   Native drag, snap, keyboard scrolling and arrow handlers remain in charge. */
(() => {
  'use strict';
  const root = document.documentElement;
  if (window.D1CarouselOverflow) return;
  const desktop = matchMedia('(min-width: 1024px)');
  const sectionSelector = '.cc-featured-collection, .cc-collection-list, .cc-recently-viewed, .cc-product-recommendations';
  const records = new Map();
  const watched = new WeakSet();
  let frame = 0;
  const rtl = () => document.dir === 'rtl';

  function schedule() {
    if (!frame) frame = requestAnimationFrame(refresh);
  }

  const sizes = typeof ResizeObserver === 'function' ? new ResizeObserver(entries => {
    entries.forEach(entry => records.forEach(record => {
      if (record.parent === entry.target && Math.abs(entry.contentRect.width - record.parentWidth) > 0.5) {
        record.parentWidth = entry.contentRect.width;
        schedule();
      }
    }));
  }) : null;

  const settings = new MutationObserver(changes => {
    changes.forEach(change => {
      if (change.target.matches('carousel-slider')) register(change.target);
    });
    schedule();
  });
  settings.observe(root, { attributes: true, attributeFilter: ['data-d1-carousel-overflow'] });

  function register(carousel) {
    const category = carousel.matches('.d1-caticons-carousel');
    if ((!category && !carousel.closest(sectionSelector)) || carousel.closest('.drawer')) return;
    if (!watched.has(carousel)) {
      watched.add(carousel);
      settings.observe(carousel, { attributes: true, attributeFilter: ['disable-desktop'] });
    }
    if (records.has(carousel) || carousel.getAttribute('disable-desktop')) return;
    const slider = carousel.querySelector('.slider');
    const grid = carousel.querySelector('.slider__grid');
    const directGrid = category && grid?.parentElement === slider;
    if (!slider || !grid || (!directGrid && !slider.querySelector(':scope > .container')) || grid.querySelectorAll(':scope > .slider__item').length < 2) return;
    const originalButtonStates = carousel.setButtonStates;
    if (typeof originalButtonStates !== 'function') return;
    const record = { carousel, slider, grid, directGrid, parent: carousel.parentElement, parentWidth: -1, lastItem: null,
      trailingOnly: category && !!carousel.closest('.collection-banner__media') };
    records.set(carousel, record);
    // The theme tests the centered content column for arrow visibility. The
    // expanded scrollport needs its actual scroll limits for reliable ends.
    carousel.setButtonStates = function () {
      if (!desktop.matches || !this.hasAttribute('data-d1-carousel-bleed')) return originalButtonStates.call(this);
      const position = Math.abs(slider.scrollLeft);
      const maximum = Math.max(0, slider.scrollWidth - slider.clientWidth);
      if (this.prevBtn) this.prevBtn.disabled = position <= 1;
      if (this.nextBtn) this.nextBtn.disabled = position >= maximum - 1;
    };
    sizes?.observe(record.parent);
    // The category app can reveal a pre-rendered row after initialization.
    if (category) settings.observe(grid, { attributes: true, attributeFilter: ['class', 'data-render-status'], childList: true });
  }

  function scan(node) {
    if (!(node instanceof Element)) return;
    if (node.matches('carousel-slider')) register(node);
    node.querySelectorAll('carousel-slider').forEach(register);
    schedule();
  }

  function refresh() {
    frame = 0;
    const viewport = root.clientWidth;
    const measurements = [];
    // Remove every enhancement before measuring to read native responsive
    // widths, including promo columns and nested container gutters.
    records.forEach((record, carousel) => {
      if (!carousel.isConnected) {
        sizes?.unobserve(record.parent);
        records.delete(carousel);
        return;
      }
      record.progress = carousel.slideSpan ? Math.abs(record.slider.scrollLeft) / carousel.slideSpan : 0;
      carousel.removeAttribute('data-d1-carousel-bleed');
      record.lastItem?.removeAttribute('data-d1-bleed-last');
    });
    records.forEach(record => {
      const { carousel, slider, grid } = record;
      if (!desktop.matches || root.dataset.d1CarouselOverflow !== 'true' || carousel.getAttribute('disable-desktop') ||
          !getComputedStyle(grid).gridAutoFlow.includes('column')) return;
      const bounds = slider.getBoundingClientRect();
      const gridBounds = grid.getBoundingClientRect();
      if (!bounds.width || !gridBounds.width) return;
      const start = rtl() ? viewport - gridBounds.right + Math.abs(slider.scrollLeft)
        : gridBounds.left + slider.scrollLeft;
      const offset = rtl() ? viewport - bounds.right : bounds.left;
      // The grid can never be wider than the room up to the viewport edge.
      let width = Math.min(grid.clientWidth, Math.floor(viewport - start));
      if (record.directGrid) {
        record.lastItem = Array.from(grid.children).filter(item => item.matches('.slider__item') && item.getClientRects().length).pop() || null;
        // The category row sits in a flex column sized by its content, so the
        // grid alone can read far too wide (36 tiles). The parent column keeps
        // its width while the bleed is applied and is the box we observe.
        const style = getComputedStyle(record.parent);
        const column = record.parent.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
        if (column > 0) width = Math.min(width, Math.floor(column));
      }
      // In the collection banner the content panel sits before the tiles, so
      // the scrollport starts at the tile column and only bleeds to the end edge.
      const lead = record.trailingOnly ? offset : 0;
      measurements.push({ record, span: viewport - lead, start: start - lead, offset: offset - lead, width,
        end: Math.max(0, viewport - start - width) });
    });
    measurements.forEach(({ record, span, start, offset, width, end }) => {
      const { carousel } = record;
      carousel.style.setProperty('--d1-bleed-viewport', span + 'px');
      carousel.style.setProperty('--d1-bleed-offset', offset.toFixed(4) + 'px');
      carousel.style.setProperty('--d1-bleed-start', start.toFixed(4) + 'px');
      carousel.style.setProperty('--d1-bleed-end', end.toFixed(4) + 'px');
      carousel.style.setProperty('--d1-bleed-grid', width + 'px');
      carousel.setAttribute('data-d1-carousel-bleed', '');
      record.lastItem?.setAttribute('data-d1-bleed-last', '');
    });
    records.forEach(({ carousel, slider, grid, progress }) => {
      if (carousel.slider !== slider || carousel.grid !== grid) return; // Native lazy init is still pending.
      carousel.handleBreakpointChange();
      const behavior = slider.style.scrollBehavior;
      slider.style.scrollBehavior = 'auto';
      slider.scrollLeft = (rtl() ? -1 : 1) * progress * carousel.slideSpan;
      slider.style.scrollBehavior = behavior;
      carousel.setButtonStates();
    });
  }

  customElements.whenDefined('carousel-slider').then(() => {
    scan(document.body);
    const observer = new MutationObserver(changes => {
      changes.forEach(change => change.addedNodes.forEach(node => {
        if (node instanceof Element && (node.matches('carousel-slider') || node.querySelector('carousel-slider'))) scan(node);
      }));
    });
    observer.observe(document.body, { childList: true, subtree: true });
  });
  window.addEventListener('resize', schedule, { passive: true });
  window.addEventListener('load', schedule, { once: true });
  desktop.addEventListener('change', schedule);
  window.D1CarouselOverflow = Object.freeze({ version: '2026-10-29.2', refresh: schedule });
})();
